import { useCallback } from 'react'
import type { ClubPermission } from '@/core/models/staff'
import { CLUB_PERMISSION_LABELS } from '@/core/models/staff'
import { useCurrentRole } from './use-current-role'

/**
 * Thrown when a mutation is invoked by a user whose role lacks the required
 * permission. Callers catch this to surface a toast; the route-level
 * `<RoleGate>` + sidebar filter normally prevent the user from reaching the
 * UI in the first place, so this is the defense-in-depth layer for custom
 * roles whose tier passes the gate but whose permissions don't.
 */
export class PermissionDeniedError extends Error {
  readonly permission: ClubPermission
  constructor(permission: ClubPermission) {
    const label = CLUB_PERMISSION_LABELS[permission] ?? permission
    super(`No tienes permiso para "${label}".`)
    this.name = 'PermissionDeniedError'
    this.permission = permission
  }
}

/**
 * Thrown when a mutation is owner-only and the caller's tier isn't `owner`.
 * Distinct from `PermissionDeniedError` because ownership isn't a
 * `ClubPermission` — several permissions (e.g. `editClubProfile`) are
 * intentionally shared by Dueño + Gerente, but a handful of actions (club
 * deletion request, module toggles) are gated on the tier itself, matching
 * `firestore.rules`' `isClubOwner()` check on the corresponding write.
 */
export class OwnerOnlyError extends Error {
  constructor() {
    super('Solo el propietario del club puede realizar esta acción.')
    this.name = 'OwnerOnlyError'
  }
}

/**
 * `assertPermission(p)` — throws `PermissionDeniedError` when the current
 * user's effective role does not have permission `p`. Mirror of
 * `useSubscriptionGate.assertCanMutate`. Platform admins pass through via
 * `useCurrentRole` (they get the synthetic owner role).
 *
 * `assertOwner()` — throws `OwnerOnlyError` when the current user's tier
 * isn't `owner`. Use for actions that are owner-only by tier rather than by
 * a shareable permission (see `OwnerOnlyError` above).
 *
 * Usage inside a mutation hook:
 *
 *     const { assertPermission } = usePermissionGate()
 *     return useMutation({
 *       mutationFn: async (input) => {
 *         assertPermission('manageCourts')
 *         // ...
 *       },
 *     })
 */
export function usePermissionGate() {
  const role = useCurrentRole()
  const assertPermission = useCallback(
    (permission: ClubPermission) => {
      if (!role.can(permission)) {
        throw new PermissionDeniedError(permission)
      }
    },
    [role],
  )
  const assertOwner = useCallback(() => {
    if (role.tier !== 'owner') {
      throw new OwnerOnlyError()
    }
  }, [role])
  return { assertPermission, assertOwner, can: role.can, tier: role.tier }
}
